import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, imgUrl } from "@/lib/api";
import { UploadButton } from "@/admin/Upload";

const input = "w-full border border-[#e2dcd4] bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#1f402b]";
const label = "block font-mono-k text-[10px] uppercase tracking-widest text-[#5a665e] mb-1.5";

export default function ListingEditor({ listing, categories, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", description: "", kind: "product", category: categories[0], price: "", region: "", images: [], ...(listing || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { title: f.title, description: f.description, kind: f.kind, category: f.category, price: f.price, region: f.region, images: f.images };
    try {
      if (listing?.id) await api.put(`/seller/listings/${listing.id}`, body);
      else await api.post("/seller/listings", body);
      toast.success(listing?.id ? "Changes sent for review" : "Advert sent for review. 1 credit used");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#fcfbfa] rounded-none" data-testid="listing-editor">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#1f402b]">{listing?.id ? "Edit advert" : "New advert"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div><span className={label}>Title</span><input required minLength={3} maxLength={90} value={f.title} onChange={set("title")} className={input} data-testid="listing-title-input" /></div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><span className={label}>Type</span>
              <select value={f.kind} onChange={set("kind")} className={input} data-testid="listing-kind-select"><option value="product">Product</option><option value="service">Service</option></select></div>
            <div><span className={label}>Category</span>
              <select value={f.category} onChange={set("category")} className={input} data-testid="listing-category-select">{categories.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><span className={label}>Price</span><input value={f.price} onChange={set("price")} placeholder="$25 or From $90/night" className={input} data-testid="listing-price-input" /></div>
          </div>
          <div><span className={label}>Region (optional)</span><input value={f.region} onChange={set("region")} className={input} data-testid="listing-region-input" /></div>
          <div><span className={label}>Description</span><textarea required minLength={10} rows={6} value={f.description} onChange={set("description")} className={input} data-testid="listing-description-input" /></div>
          <div>
            <span className={label}>Photos (up to 6)</span>
            <div className="flex flex-wrap gap-2 mb-3">
              {f.images.map((u, i) => (
                <div key={u} className="relative w-24 h-20">
                  <img src={imgUrl(u)} alt="" className="w-full h-full object-cover border border-[#e2dcd4]" />
                  <button type="button" onClick={() => setF({ ...f, images: f.images.filter((_, j) => j !== i) })} className="absolute -top-2 -right-2 bg-white border border-[#e2dcd4] p-0.5" data-testid={`listing-image-remove-${i}`}><X size={12} /></button>
                </div>
              ))}
            </div>
            {f.images.length < 6 && <UploadButton multiple label="Add photos" testid="listing-photo-upload" onUploaded={(url) => setF((p) => ({ ...p, images: [...p.images, url].slice(0, 6) }))} />}
          </div>
          <p className="text-xs text-[#5a665e]">Sun, beach and naturist-lifestyle items only. Nothing sexual or adult. Every advert, and every edit, is checked before it goes live.</p>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm border border-[#e2dcd4]" data-testid="listing-cancel">Cancel</button>
            <button disabled={saving} className="px-6 py-2.5 text-sm bg-[#1f402b] text-white hover:bg-[#2c5a3d] disabled:opacity-60" data-testid="listing-save">
              {saving ? "Sending…" : listing?.id ? "Save & resubmit" : "Submit for review (1 credit)"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
